import { ParamMap, Params } from '@angular/router';
import { IBase } from 'src/app/models';
import { ExpeditionStatus, ExpeditionType } from 'src/app/models/enums';
import {
  hasAnyFilterKey,
  parseEnumList,
  parseIdList,
  serializeIdList,
  serializeStringList,
} from './filterUtils';

export interface IBookingFilters {
  selectedClients: IBase[];
  selectedTowns: IBase[];
  selectedServices: IBase[];
  selectedRoutes: IBase[];
  selectedTypes: ExpeditionType[];
  selectedStatus: ExpeditionStatus[];
  cancelled: boolean;
  faults: boolean;
  origin: string;
  destination: string;
}

export const BOOKING_FILTER_KEYS = [
  'clients', 'towns', 'services', 'routes',
  'types', 'status', 'cancelled', 'faults',
  'origin', 'destination'
] as const;

export const DEFAULT_BOOKING_STATUS: ExpeditionStatus[] = [
  ExpeditionStatus.OPEN, ExpeditionStatus.READY, ExpeditionStatus.ONGOING
];

export const USER_DEFAULT_BOOKING_STATUS: ExpeditionStatus[] = [ExpeditionStatus.CLOSED];

export function emptyBookingFilters(defaultStatus = DEFAULT_BOOKING_STATUS): IBookingFilters {
  return {
    selectedClients: [],
    selectedTowns: [],
    selectedServices: [],
    selectedRoutes: [],
    selectedTypes: [],
    selectedStatus: defaultStatus,
    cancelled: false,
    faults: false,
    origin: '',
    destination: '',
  };
}

export function serializeBookingFilters(f: IBookingFilters): Params {
  const out: Params = {};
  out['clients'] = serializeIdList(f.selectedClients);
  out['towns'] = serializeIdList(f.selectedTowns);
  out['services'] = serializeIdList(f.selectedServices);
  out['routes'] = serializeIdList(f.selectedRoutes);
  out['types'] = serializeStringList(f.selectedTypes);
  out['status'] = serializeStringList(f.selectedStatus);
  out['cancelled'] = f.cancelled ? 'true' : null;
  out['faults'] = f.faults ? 'true' : null;
  out['origin'] = f.origin || null;
  out['destination'] = f.destination || null;
  return out;
}

export function deserializeBookingFilters(
  params: ParamMap,
  fallback?: IBookingFilters
): IBookingFilters {
  if (!hasAnyFilterKey(params, BOOKING_FILTER_KEYS) && fallback) return fallback;

  return {
    selectedClients: parseIdList(params.get('clients'), fallback?.selectedClients),
    selectedTowns: parseIdList(params.get('towns'), fallback?.selectedTowns),
    selectedServices: parseIdList(params.get('services'), fallback?.selectedServices),
    selectedRoutes: parseIdList(params.get('routes'), fallback?.selectedRoutes),
    selectedTypes: parseEnumList<ExpeditionType>(params.get('types')),
    selectedStatus: parseEnumList<ExpeditionStatus>(params.get('status'),
      fallback?.selectedStatus ?? DEFAULT_BOOKING_STATUS),
    cancelled: params.get('cancelled') === 'true',
    faults: params.get('faults') === 'true',
    origin: params.get('origin') ?? '',
    destination: params.get('destination') ?? '',
  };
}

export function parseBookingFiltersFromStorage(json: string | null | undefined): IBookingFilters | null {
  if (!json) return null;
  try {
    const f = JSON.parse(json);
    return {
      selectedClients: f.selectedClients ?? [],
      selectedTowns: f.selectedTowns ?? [],
      selectedServices: f.selectedServices ?? [],
      selectedRoutes: f.selectedRoutes ?? [],
      selectedTypes: f.selectedTypes ?? [],
      selectedStatus: f.selectedStatus?.length ? f.selectedStatus : DEFAULT_BOOKING_STATUS,
      cancelled: !!f.cancelled,
      faults: !!f.faults,
      origin: f.origin ?? '',
      destination: f.destination ?? '',
    };
  } catch {
    return null;
  }
}
